import { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

function envModeColor(mode: string): string {
  if (mode === "production" || mode === "live") return "bg-danger";
  if (mode === "staging") return "bg-warning";
  return "bg-info";
}

function envModeLabel(mode: string): string {
  if (mode === "production" || mode === "live") return "Production";
  if (mode === "staging") return "Staging";
  if (mode === "sandbox") return "Sandbox";
  return mode.charAt(0).toUpperCase() + mode.slice(1);
}

export default function OrgSwitcher() {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const experience = useAuthStore((s) => s.experience);
  const selectOrganization = useAuthStore((s) => s.selectOrganization);
  const { activeOrganization, memberships, environment } = experience;
  const isApiSource = experience.source === "api";

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    function onPointerDown(e: MouseEvent | TouchEvent) {
      if (!rootRef.current?.contains(e.target as Node)) close();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close]);

  function handleSelect(membershipId: string) {
    if (membershipId !== activeOrganization.id) {
      void selectOrganization(membershipId);
    }
    close();
  }

  const envLabel = envModeLabel(environment.mode);
  const isProduction = environment.mode === "production" || environment.mode === "live";

  return (
    <div ref={rootRef} className="relative" data-testid="org-switcher">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Organization · ${activeOrganization.label} · ${envLabel}`}
        data-testid="org-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 items-center gap-2 rounded-md border border-transparent bg-bg px-2.5 py-1 text-left transition-colors hover:border-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
      >
        <Avatar
          initials={activeOrganization.label.slice(0, 2).toUpperCase()}
          bgColor="var(--ads-primary)"
          size={24}
          fontSize={9}
        />
        <span className="hidden min-w-0 items-center gap-1.5 text-sm font-semibold text-ink md:flex">
          <span className="max-w-[180px] truncate">{activeOrganization.label}</span>
          <span className="text-muted">·</span>
          <span className={cn("truncate", isProduction ? "text-danger" : "text-info")}>
            {envLabel}
          </span>
        </span>
        {memberships.length > 1 && (
          <ChevronDown
            aria-hidden
            className={cn("size-4 shrink-0 text-muted transition-transform", open && "rotate-180")}
          />
        )}
      </button>

      {open && memberships.length > 1 && (
        <div
          role="menu"
          aria-label="Switch organization"
          data-testid="org-switcher-menu"
          className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-line bg-surface shadow"
        >
          <div className="border-b border-line px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted uppercase tracking-[0.06em]">
              <Building2 aria-hidden className="size-3.5" />
              Organization
            </div>
          </div>
          <div className="max-h-[min(24rem,80vh)] overflow-y-auto p-1.5">
            {memberships.map((membership) => {
              const active = membership.id === activeOrganization.id;
              return (
                <button
                  key={membership.id}
                  type="button"
                  role="menuitem"
                  data-testid={`org-option-${membership.id}`}
                  onClick={() => handleSelect(membership.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-sm px-3 py-2.5 text-left text-sm outline-none transition-colors hover:bg-bg focus-visible:ring-2 focus-visible:ring-primary-soft",
                    active && "bg-primary-soft text-ink",
                  )}
                >
                  <Avatar
                    initials={membership.label.slice(0, 2).toUpperCase()}
                    bgColor="var(--ads-primary)"
                    size={28}
                    fontSize={10}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold text-ink">{membership.label}</span>
                  </span>
                  {active && <Check aria-hidden className="size-4 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>
          {isApiSource && (
            <div className="border-t border-line px-4 py-2 text-xs text-muted">
              <span className={cn("mr-1.5 inline-block size-2 rounded-full", envModeColor(environment.mode))} />
              {envLabel} · {environment.deploymentProfile}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
